import type { BoxProps } from '@mui/material/Box';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import CircularProgress from '@mui/material/CircularProgress';
import { Divider, ListItem, ButtonBase, ListItemAvatar } from '@mui/material';

// ----------------------------------------------------------------------

type ChatMessageSkeletonProps = BoxProps & {
  itemCount?: number;
};

export function ChatNavItemSkeleton({ sx, itemCount = 6, ...other }: ChatMessageSkeletonProps) {
  return Array.from({ length: itemCount }, (_, index) => (
    <Box
      key={index}
      sx={[
        () => ({
          gap: 2,
          px: 2.5,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      <Skeleton variant="circular" sx={{ width: 48, height: 48 }} />

      <Stack spacing={1} flexGrow={1}>
        <Skeleton sx={{ width: 0.75, height: 10 }} />
        <Skeleton sx={{ width: 0.5, height: 10 }} />
      </Stack>
    </Box>
  ));
}

// ----------------------------------------------------------------------

export function ChatHeaderSkeleton({ sx, ...other }: BoxProps) {
  return (
    <Box
      sx={[{ width: 1, display: 'flex', alignItems: 'center' }, ...(Array.isArray(sx) ? sx : [sx])]}
      {...other}
    >
      <Skeleton variant="circular" sx={{ width: 40, height: 40 }} />
      <Stack spacing={1} flexGrow={1} sx={{ mx: 2 }}>
        <Skeleton sx={{ width: 96, height: 10 }} />
        <Skeleton sx={{ width: 40, height: 10 }} />
      </Stack>

      <Skeleton variant="circular" sx={{ width: 28, height: 28 }} />
      <Skeleton variant="circular" sx={{ width: 28, height: 28, mx: 1 }} />
      <Skeleton variant="circular" sx={{ width: 28, height: 28, mr: 1 }} />
    </Box>
  );
}

// ----------------------------------------------------------------------

export function ChatRoomSkeleton({ sx, ...other }: BoxProps) {
  return (
    <Box
      sx={[
        () => ({
          pt: 5,
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      <Stack alignItems="center">
        <Skeleton variant="circular" sx={{ width: 96, height: 96 }} />
        <Skeleton
          sx={{
            mb: 1,
            mt: 2,
            height: 10,
            width: 0.65,
          }}
        />
        <Skeleton sx={{ mb: 5, width: 0.35, height: 10 }} />
        <CircularProgress color="inherit" thickness={2} />
      </Stack>
    </Box>
  );
}

export function ChatMessageSkeleton({ sx, ...other }: BoxProps) {
  return (
    <Box
      sx={[
        () => ({
          pt: 5,
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          px: 2,
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      {[...Array(8)].map((_, index) => (
        <Stack
          key={index}
          direction="row"
          justifyContent={index % 2 === 0 ? 'flex-start' : 'flex-end'}
        >
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 2,
              p: 1.5,
              maxWidth: '70%',
              minWidth: '40%',
            }}
          >
            <Skeleton variant="text" height={40} width="100%" />
          </Box>
        </Stack>
      ))}
    </Box>
  );
}

export const TemplateSkeleton = () => (
  <ButtonBase
    disabled
    sx={{
      py: 1.5,
      my: 1,
      px: 2,
      gap: 1,
      width: 1,
      borderRadius: 2,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      bgcolor: 'background.paper',
      boxShadow: 1,
    }}
  >
    {/* Name and Status Skeleton */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
      <Skeleton variant="text" width="40%" height={24} />
      <Skeleton variant="rounded" width={60} height={24} />
    </Box>

    {/* Category and Date Skeleton */}
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, width: '100%' }}>
      <Skeleton variant="text" width="30%" height={20} />
      <Skeleton variant="text" width="25%" height={20} />
    </Box>
  </ButtonBase>
);

export function ChatAssigneeSkeleton({ sx, itemCount = 6, ...other }: any) {
  return Array.from({ length: itemCount }, (_, index) => (
    <Box
      key={index}
      sx={[
        {
          display: 'flex',
          alignItems: 'center',
          px: 2.5,
          py: 1.5,
          gap: 2,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      <Skeleton variant="circular" sx={{ width: 40, height: 40 }} />
      <Stack spacing={0.5} flexGrow={1}>
        <Skeleton variant="text" sx={{ width: '40%', height: 12 }} />
        <Skeleton variant="text" sx={{ width: '30%', height: 10 }} />
      </Stack>
    </Box>
  ));
}

export const SkeletonNotificationItem = () => (
  <>
    <ListItem alignItems="flex-start">
      <ListItemAvatar>
        <Skeleton variant="circular" width={40} height={40} />
      </ListItemAvatar>
      <Box sx={{ flex: 1 }}>
        <Skeleton variant="text" width="80%" height={20} />
        <Skeleton variant="text" width="60%" height={16} />
        <Skeleton variant="text" width="30%" height={14} />
      </Box>
    </ListItem>
    <Divider variant="inset" component="li" />
  </>
);
